import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Item } from '@boodschappenlijst/shared';
import { ProductForm } from '../../components/ProductForm';
import type { ProductFormWaarden } from '../../components/ProductForm';
import { useLijsten, useSuggesties } from '../../lib/useLijsten';
import { VasteProducten } from '../vast/VasteProducten';
import { BestelBijAh } from './BestelBijAh';
import { BoodschappenLijst } from './BoodschappenLijst';
import type { Filter } from './BoodschappenLijst';
import { DuplicaatDialog } from './DuplicaatDialog';
import { itemsApi } from './items.api';

/** Openstaande duplicaatvraag: het gevonden item plus wat de gebruiker invulde. */
interface Duplicaat {
  match: Item;
  nieuw: ProductFormWaarden;
}

/** De boodschappen-tab: toevoegen, de lijst zelf, en de vaste producten. */
export function BoodschappenPage() {
  const { items, ververs, meldFout, toast } = useLijsten();
  const suggesties = useSuggesties();

  const [filter, setFilter] = useState<Filter>('open');
  const [duplicaat, setDuplicaat] = useState<Duplicaat | null>(null);
  /** Het `resolve` van de `probeerToevoegen` die op de duplicaatvraag wacht.
   *  Hieraan hangt of het formulier wordt leeggemaakt. */
  const beantwoordDuplicaat = useRef<((toegevoegd: boolean) => void) | null>(null);

  const voegToe = useMutation({
    mutationFn: itemsApi.create,
    onSuccess: () => {
      ververs();
      toast('Toegevoegd aan de lijst');
    },
    onError: meldFout,
  });

  const samenvoegen = useMutation({
    mutationFn: ({ id, aantal }: { id: string; aantal: number }) =>
      itemsApi.samenvoegen(id, aantal),
    onSuccess: () => {
      ververs();
      toast('Bij de bestaande regel opgeteld');
    },
    onError: meldFout,
  });

  const toggle = useMutation({
    mutationFn: itemsApi.toggleGedaan,
    onSuccess: ververs,
    onError: meldFout,
  });

  const verwijder = useMutation({
    mutationFn: itemsApi.remove,
    onSuccess: ververs,
    onError: meldFout,
  });

  const wisGedaan = useMutation({
    mutationFn: itemsApi.removeGedaan,
    onSuccess: ({ verwijderd }) => {
      ververs();
      toast(`${verwijderd} regel${verwijderd === 1 ? '' : 's'} van de lijst gehaald`);
    },
    onError: meldFout,
  });

  /** Sluit de duplicaatvraag en vertelt het wachtende formulier of de regel er
   *  nu op staat — zo ja, dan mag de invoer weg. */
  function sluitDuplicaat(toegevoegd: boolean) {
    setDuplicaat(null);
    beantwoordDuplicaat.current?.(toegevoegd);
    beantwoordDuplicaat.current = null;
  }

  /** Eerst vragen of het product er al op staat. Zo ja: de vraag stellen en op
   *  het antwoord wachten. Het formulier blijft ondertussen staan, zodat de
   *  invoer niet verdwijnt terwijl de gebruiker nog moet kiezen — en wordt pas
   *  leeggemaakt als de regel er daadwerkelijk op staat. Zonder dat wachten
   *  bleef de vorige productnaam achter, en plakte het volgende dat iemand
   *  typte eraan vast. */
  async function probeerToevoegen(waarden: ProductFormWaarden): Promise<boolean> {
    try {
      const { matches } = await itemsApi.check(waarden.naam);
      const match = matches[0];
      if (match) {
        setDuplicaat({ match, nieuw: waarden });
        return await new Promise<boolean>((resolve) => {
          beantwoordDuplicaat.current = resolve;
        });
      }
    } catch {
      // De dubbelcheck is een hulpmiddel, geen voorwaarde: als hij faalt voegen
      // we gewoon toe. Een dubbele regel is minder erg dan niets kunnen opschrijven.
    }
    await voegToe.mutateAsync(waarden);
    return true;
  }

  return (
    <>
      <section>
        <h2 className="vs-section-title">Toevoegen</h2>
        <div className="vs-card">
          <ProductForm
            idPrefix="item"
            knopLabel="Op de lijst"
            bezig={voegToe.isPending}
            suggesties={suggesties.data?.suggesties}
            onSubmit={probeerToevoegen}
          />
        </div>
      </section>

      {items.isPending ? (
        <p className="vs-empty">Laden…</p>
      ) : items.isError ? (
        <p className="vs-empty">De lijst kon niet worden geladen.</p>
      ) : (
        <BoodschappenLijst
          items={items.data.items}
          filter={filter}
          onFilter={setFilter}
          onToggle={(id) => toggle.mutate(id)}
          onVerwijder={(id) => verwijder.mutate(id)}
          onWisGedaan={() => wisGedaan.mutate()}
        />
      )}

      {items.isSuccess && <BestelBijAh items={items.data.items} />}

      <VasteProducten onGewijzigd={ververs} />

      {duplicaat && (
        <DuplicaatDialog
          match={duplicaat.match}
          nieuw={duplicaat.nieuw}
          bezig={samenvoegen.isPending || voegToe.isPending}
          onAnnuleren={() => sluitDuplicaat(false)}
          onSamenvoegen={async () => {
            await samenvoegen.mutateAsync({
              id: duplicaat.match.id,
              aantal: duplicaat.nieuw.aantal,
            });
            sluitDuplicaat(true);
          }}
          onApart={async () => {
            await voegToe.mutateAsync(duplicaat.nieuw);
            sluitDuplicaat(true);
          }}
        />
      )}
    </>
  );
}
